import { z } from 'zod';

/**
 * Zod schema for validating OAuth redirect URLs.
 * Ensures the URL:
 * - Is a valid URL format
 * - Uses HTTPS protocol (required for security)
 * - Has a valid hostname
 */
const oAuthRedirectUrlSchema = z
  .string()
  .url('Invalid URL format')
  .refine(
    url => {
      try {
        const parsedUrl = new URL(url);
        // Only allow HTTPS protocol for security
        return parsedUrl.protocol === 'https:';
      } catch {
        return false;
      }
    },
    {
      message: 'URL must use HTTPS protocol',
    },
  )
  .refine(
    url => {
      try {
        const parsedUrl = new URL(url);
        const hostname = parsedUrl.hostname;
        // Ensure hostname exists, is not empty, and looks like a domain
        // Allow localhost for development, or hostnames with dots (real domains)
        if (!hostname || hostname.trim() === '') {
          return false;
        }
        // Check if it's localhost or contains a dot (indicating a domain)
        return (
          hostname === 'localhost' ||
          hostname.includes('.') ||
          /^\[.*\]$/.test(hostname) // IPv6 addresses in brackets
        );
      } catch {
        return false;
      }
    },
    {
      message: 'URL must have a valid domain hostname',
    },
  );

/**
 * Validates that a URL is safe for redirect operations.
 * Ensures the URL uses HTTPS and is a valid HTTP/HTTPS URL.
 *
 * @param url - The URL string to validate
 * @returns true if the URL is safe for redirect, false otherwise
 *
 * @example
 * isValidRedirectUrl('https://oauth.provider.com/authorize') // true
 * isValidRedirectUrl('http://example.com') // false (HTTP not allowed)
 * isValidRedirectUrl('javascript:alert(1)') // false (dangerous scheme)
 */
export function isValidRedirectUrl(url: string): boolean {
  const result = oAuthRedirectUrlSchema.safeParse(url);
  return result.success;
}

/**
 * Validates an OAuth authorization URL before redirecting.
 * Throws an error if the URL is invalid or unsafe.
 *
 * @param url - The OAuth authorization URL to validate
 * @throws Error if the URL is invalid or unsafe
 *
 * @example
 * validateOAuthRedirectUrl('https://oauth.provider.com/authorize?client_id=123')
 * // No error thrown for valid HTTPS URL
 *
 * validateOAuthRedirectUrl('http://example.com')
 * // Throws: Error with Zod validation message
 */
export function validateOAuthRedirectUrl(url: string): void {
  const result = oAuthRedirectUrlSchema.safeParse(url);
  if (!result.success) {
    // Use the first error message from Zod, or fallback to generic message
    const errorMessage =
      result.error.issues[0]?.message || 'Invalid or unsafe OAuth redirect URL';
    throw new Error(errorMessage);
  }
}
